import { trainerRoleLabel } from './trainerUtils';
import React, { useState } from 'react';
import {
  Users, UserCheck, AlertTriangle, MessageSquare,
  ClipboardCheck, Eye, RotateCcw,
} from 'lucide-react';
// Course materials — trainers and L&D upload, everyone on the session downloads.
import SessionMaterials from './SessionMaterials';
import { formatDate, formatDateTime, formatDateShort } from '../../utils/dateFormat';
// The post-course quiz: a person's sittings, read here question by question.
import QuizAttemptModal from './QuizAttemptModal';
import {
  fmtDuration, fmtPercent, QuizVerdictBadge, QuizStatusBadge, QUIZ_STATUS_ORDER, QUIZ_MODE_LABELS,
} from './quizFormat';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import StatTile, { SESSION_STAT_HINTS } from './StatTile';
import VenueCheckBadge from './VenueCheckBadge';
import { describeAudienceRules } from './audienceRuleMeta';
import { InfoTip } from '../shared/FormKit';
// Score → label wording shared with TrainingFeedback.js / FeedbackForm.js so a
// score reads the same on every surface.
import { EFFECTIVENESS_LABELS, COMPETENCY_SHORT, scoreLabel } from '../../utils/feedbackScales';

/**
 * Everything about one sitting: a summary strip (session facts + check-in
 * stats) and sub-tabs for Expected / Attendance / Absence Reasons / Feedback.
 *
 * Fed by `GET /api/ld/training-sessions/:id/detail`. Lives on its own
 * because two surfaces show it and they must stay identical:
 *   - the dashboard's Training Sessions tab, in an expanded row
 *   - L&D Records → Sessions, in the row's View modal
 *
 * All four tables sort by column and page PAGE_SIZE at a time (the
 * house rule for every table) via usePagedSortableData/TablePager.
 *
 * Props: detail (the endpoint's body), tab, onTab — the caller owns the tab
 * state so it can reset it when a different session is opened.
 * `onResetAttempt(staffId)` (optional) turns on the Quiz tab's Reset action —
 * the Training Records modal passes it (ld:training:edit); the dashboard
 * does not, so its panel stays read-only.
 */

const score = scoreLabel;

// Roster row → badge wording/colour. 'pending' is the one HR chases.
const ROSTER_STATUS = {
  attended: { label: 'Attended', cls: 'bg-green-100 text-green-800' },
  absent: { label: 'Absent (filed)', cls: 'bg-amber-100 text-amber-800' },
  pending: { label: 'No response', cls: 'bg-slate-100 text-slate-600' },
};
// Sorting Status by the badge text would read Absent → Attended → No response;
// this keeps the chase-list order (still owing a response first).
const STATUS_ORDER = { pending: 0, absent: 1, attended: 2 };

// Head-count strip: large-screen columns and width cap by how many tiles are
// on it (5 base tiles; Walk-Ins and Quiz Pass Rate each add one). Spelled out
// in full so Tailwind can see every class.
const TILE_GRID = {
  5: 'lg:grid-cols-5 max-w-3xl',
  6: 'lg:grid-cols-6 max-w-4xl',
  7: 'lg:grid-cols-7 max-w-5xl',
};

const EMPTY = [];

// `onFilesChange()` (optional) turns on upload / remove on the Course
// Materials block — the Training Records modal passes it (ld:training:edit);
// the dashboard does not, so its panel stays read-only.
const SessionDetailPanel = ({ detail, tab, onTab, onResetAttempt = null, onFilesChange = null }) => {
  const { session, attendance, absences, feedback, stats } = detail;
  const files = detail.files || EMPTY;
  const recordings = detail.recordings || EMPTY;
  // Older responses to this endpoint had no roster — default so the panel
  // still renders rather than throwing on .length.
  const roster = detail.roster || EMPTY;
  // The post-course quiz panel — null when the session carries no quiz.
  const quiz = detail.quiz || null;
  const quizRows = quiz ? quiz.results : EMPTY;

  // One hook per table; all five run every render (hooks may not be conditional)
  // but only the active tab's rows are drawn.
  const rosterTable = usePagedSortableData(roster, { key: 'name', direction: 'asc' });
  const attendanceTable = usePagedSortableData(attendance, { key: 'name', direction: 'asc' });
  const absenceTable = usePagedSortableData(absences, { key: 'name', direction: 'asc' });
  const feedbackTable = usePagedSortableData(feedback, { key: 'name', direction: 'asc' });
  const quizTable = usePagedSortableData(quizRows, { key: 'name', direction: 'asc' });
  const [viewAttempt, setViewAttempt] = useState(null);   // { staffId, name }
  const [resetting, setResetting] = useState(null);       // staffId in flight
  const [resetError, setResetError] = useState(null);

  const resetAttempt = async (r) => {
    if (!onResetAttempt) return;
    setResetting(r.staffId);
    setResetError(null);
    try { await onResetAttempt(r.staffId); } catch (e) { setResetError(e.message); }
    finally { setResetting(null); }
  };

  const avg = (list, key) => {
    const vals = list.map((r) => r[key]).filter((v) => v != null);
    if (vals.length === 0) return null;
    return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
  };

  // "How many are still silent" reads better the other way
  // round — how many of the expected list have answered at all (checked in OR
  // filed an absence). `pending` is exactly the silent ones, so expected minus
  // pending is the answered count; both come from checkinStats().
  const respondedLabel = stats?.expected != null && stats?.pending != null
    ? `${stats.expected - stats.pending}/${stats.expected}`
    : '—';

  // "At venue X/Y": of the check-ins, how many were
  // GPS-verified at the venue. Computed from the attendance rows this panel
  // already holds (trainer's booking excluded — it is not a scan). '—' when
  // the session has no venue coordinates: nothing was ever measured.
  const scans = attendance.filter((r) => !r.isTrainer);
  const atVenueCount = scans.filter((r) => r.checkinInRange === true).length;
  const measurable = session.venueLatitude != null && session.venueRadiusM != null;
  const atVenueLabel = measurable && scans.length > 0 ? `${atVenueCount}/${scans.length}` : '—';

  // Walk-ins get a tile only when there are any.
  const hasWalkIns = (stats?.walkIns ?? 0) > 0;

  const subTabs = [
    { id: 'roster', label: `Expected (${roster.length})`, icon: Users },
    { id: 'attendance', label: `Attendance (${attendance.length})`, icon: UserCheck },
    { id: 'absences', label: `Absence Reasons (${absences.length})`, icon: AlertTriangle },
    { id: 'feedback', label: `Feedback (${feedback.length})`, icon: MessageSquare },
    // Only when the session carries a quiz — "done / assigned" (done =
    // passed, or submitted on a score-only paper; Not passed is still open).
    ...(quiz ? [{ id: 'quiz', label: `Quiz (${quiz.stats.done}/${quiz.stats.assigned})`, icon: ClipboardCheck }] : []),
  ];

  const chip = (label, value) => (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
  );

  // Time / Venue / each trainer, as one middot-separated line.
  // The boxes stay reserved for the head counts.
  // A trainer prints as name, role and factor — "Ann Lee · Lead Trainer ×1" —
  // because the factor is each trainer's own, not the session's.
  const facts = [
    session.startEndTime ? { text: session.startEndTime } : null,
    // Where it was held. Absent for online/off-site sessions and for
    // anything recorded before the venue list existed, so it simply drops out
    // of the row rather than printing a dash.
    session.venueName ? { text: session.venueName } : null,
    ...(session.trainers || []).map((t) => ({
      text: `${t.name} · ${trainerRoleLabel(t.role)} ×${t.factor}`,
      title: 'Trainer hours = session hours × factor',
    })),
  ].filter(Boolean);

  // Who was supposed to come, in words. The audience is a set of rules,
  // so the rules themselves are what gets shown.
  const ruleLines = describeAudienceRules(session.audienceRules);
  const removedCount = session.audienceRules?.exclude?.staffIds?.length || 0;

  // "Department: QM" → a two-tone chip (grey label, dark value) so a screen
  // full of rules can be scanned by its values. A rule that combines
  // conditions ("Department: Audit · Grade: Senior") keeps them in one chip,
  // each as its own label/value pair. Rules with no colon ("All active
  // staff") print as one piece.
  const ruleChip = (line, i) => (
    <span
      key={i}
      className="inline-flex items-center gap-1.5 max-w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs"
    >
      {line.split(' · ').map((part, j) => {
        const at = part.indexOf(': ');
        return (
          <span key={j} className="inline-flex items-center gap-1 min-w-0">
            {j > 0 && <span className="text-gray-300 shrink-0 mr-0.5">|</span>}
            {at > 0 && <span className="text-gray-500 shrink-0">{part.slice(0, at)}</span>}
            <span className="font-medium text-gray-900 truncate">{at > 0 ? part.slice(at + 2) : part}</span>
          </span>
        );
      })}
    </span>
  );

  const th = 'px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider';
  const td = 'px-4 py-2 text-sm text-gray-700';
  const pagerCls = 'flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50';

  return (
    <div className="space-y-4">
      {/* One summary card, two rows: the sitting's facts, then who was expected
          and on what grounds. Deliberately not three stacked blocks — their
          contents would largely repeat one another and the row above. */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {/* The sitting, at a glance */}
        <div className="flex flex-wrap items-center gap-y-1.5 px-3 py-2">
          {facts.length === 0 ? (
            <span className="text-sm text-gray-500">No time or trainer recorded.</span>
          ) : facts.map(({ text, title }, i) => (
            <React.Fragment key={text}>
              {i > 0 && <span className="mx-1.5 text-gray-300">·</span>}
              <span className="text-sm text-gray-700" title={title}>{text}</span>
            </React.Fragment>
          ))}
        </div>

        {/* Who was expected. Chips only — the rules speak for themselves;
            no note explaining how they combine. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500 shrink-0">
            Expected Attendees
          </span>
          {ruleLines.length === 0 ? (
            <span className="text-sm text-gray-500">Put together by hand — no conditions set</span>
          ) : (
            ruleLines.map(ruleChip)
          )}
          {removedCount > 0 && (
            <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
              {removedCount} removed by hand
            </span>
          )}
        </div>

        {/* Completed = the session and its attendee list are frozen, and every
            figure below is read from the snapshot taken that day. */}
        {session.completed && (
          <p className="px-3 py-2 text-xs text-gray-500 bg-gray-50 rounded-b-lg">
            <span className="font-medium text-gray-700">Completed{session.completedDate ? ` on ${formatDate(session.completedDate)}` : ''}.</span>
            {' '}The record and its attendee list are frozen — designations and departments below are
            the ones held that day.
          </p>
        )}
      </div>

      {/* Course materials (docs/modules/ld.md §3.6): what the trainers put up
          for the participants. L&D can add or remove them here. */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Course Materials</span>
          <span className="text-xs text-gray-500">({files.length})</span>
        </div>
        <SessionMaterials
          sessionId={session.id}
          files={files}
          compact
          canManage={!!onFilesChange}
          onChanged={() => { if (onFilesChange) onFilesChange(); }}
        />
      </div>

      {/* The recording — the course video (uploaded or linked) added after it ran. */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Recording</span>
          <span className="text-xs text-gray-500">({recordings.length})</span>
        </div>
        <SessionMaterials
          sessionId={session.id}
          kind="recording"
          files={recordings}
          compact
          canManage={!!onFilesChange}
          onChanged={() => { if (onFilesChange) onFilesChange(); }}
        />
      </div>

      {/* Head counts — the QR screen's tiles plus the GPS verdict:
          of the check-ins, how many were verified at the venue. The Walk-Ins
          tile shows only when there is at least one (a standing "0" is
          noise), and a session carrying a quiz gets one more: its pass rate,
          right here on the summary strip (the Quiz tab holds the breakdown).
          Width-capped so the tiles stay a readable group instead of
          stretching across a wide screen with the number floating away from
          its label. */}
      <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${TILE_GRID[5 + (hasWalkIns ? 1 : 0) + (quiz ? 1 : 0)]}`}>
        <StatTile label="Expected" value={stats?.expected ?? '—'} tone="slate" />
        {/* Checked In = expected people who turned up, out of the expected
            list; walk-ins (not on the list) are counted on their own tile so
            a full house of walk-ins cannot read as "everyone came". */}
        <StatTile
          label="Checked In"
          value={stats?.attendedExpected != null ? `${stats.attendedExpected}/${stats.expected}` : '—'}
          tone={stats?.attendedExpected == null || stats.expected === 0 ? 'slate'
            : (stats.attendedExpected >= stats.expected ? 'green' : 'amber')}
        />
        {hasWalkIns && (
          <StatTile label="Walk-Ins" value={stats.walkIns} tone="slate" hint={SESSION_STAT_HINTS.walkIns} />
        )}
        <StatTile label="Absent (Filed)" value={stats?.absent ?? '—'} tone="amber" />
        <StatTile label="Response" value={respondedLabel} tone="slate" />
        <StatTile
          label="At Venue"
          value={atVenueLabel}
          tone={atVenueLabel === '—' ? 'slate' : (atVenueCount < scans.length ? 'amber' : 'green')}
        />
        {/* '—' = nothing gradable yet (no submissions, or a score-only quiz
            with no pass mark and no must-answer questions). */}
        {quiz && (
          <StatTile
            label="Quiz Pass Rate"
            value={quiz.stats.passRate == null ? '—' : `${quiz.stats.passRate}%`}
            tone={quiz.stats.passRate == null ? 'slate' : (quiz.stats.passRate < 100 ? 'amber' : 'green')}
          />
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        {subTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onTab(t.id)}
              className={`pb-2 text-sm font-medium flex items-center gap-1.5 ${
                tab === t.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 border-b-2 border-transparent hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Expected — everyone on the list, answered or not */}
      {tab === 'roster' && (
        roster.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">
            Nobody is bound to this session yet — generate the expected list from the check-in screen.
          </p>
        ) : (
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="Staff" sortKey="name" sortConfig={rosterTable.sortConfig} onSort={rosterTable.requestSort} className={th} />
                  <SortableHeader label="Designation" sortKey="position" sortConfig={rosterTable.sortConfig} onSort={rosterTable.requestSort} className={th} />
                  <SortableHeader label="Department" sortKey="department" sortConfig={rosterTable.sortConfig} onSort={rosterTable.requestSort} className={th} />
                  <SortableHeader label="Status" sortKey="status" sortConfig={rosterTable.sortConfig} onSort={rosterTable.requestSort} accessor={(r) => STATUS_ORDER[r.status] ?? 9} className={th} />
                  <SortableHeader label="Why Expected" sortKey="ruleLabel" sortConfig={rosterTable.sortConfig} onSort={rosterTable.requestSort} className={th} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rosterTable.rows.map((r) => {
                  const s = ROSTER_STATUS[r.status] || ROSTER_STATUS.pending;
                  return (
                    <tr key={r.staffId}>
                      <td className={td}>
                        <div className="font-medium text-gray-900">
                          {r.name}
                          {r.isTrainer && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">Trainer</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{r.email}</div>
                      </td>
                      <td className={td}>{r.position || '—'}</td>
                      <td className={td}>{r.department || '—'}</td>
                      <td className={td}>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${s.cls}`}>
                          {s.label}
                        </span>
                        {/* An attended row that scanned from outside the venue
                            says so right on the status. */}
                        {r.status === 'attended' && r.checkinInRange === false && (
                          <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-xs rounded bg-amber-50 text-amber-800 whitespace-nowrap">
                            Outside venue
                          </span>
                        )}
                      </td>
                      <td className={`${td} text-gray-500`}>{r.ruleLabel || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePager {...rosterTable} className={pagerCls} />
            {/* Explains why this count can be one more than the Expected chip:
                the trainer is listed too but is not part of the bound list. */}
            <p className="text-xs text-gray-500 px-4 py-2 border-t border-gray-100">
              This session&apos;s bound attendee list plus the trainer. Walk-ins (checked in without
              being on the list) are on the Attendance tab.
              <InfoTip className="ml-1" text="No response = neither checked in nor filed an absence." />
            </p>
          </div>
        )
      )}

      {/* Attendance */}
      {tab === 'attendance' && (
        attendance.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No attendance recorded yet.</p>
        ) : (
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="Staff" sortKey="name" sortConfig={attendanceTable.sortConfig} onSort={attendanceTable.requestSort} className={th} />
                  <SortableHeader label="Designation" sortKey="position" sortConfig={attendanceTable.sortConfig} onSort={attendanceTable.requestSort} className={th} />
                  <SortableHeader label="Department" sortKey="department" sortConfig={attendanceTable.sortConfig} onSort={attendanceTable.requestSort} className={th} />
                  <SortableHeader label="Training Hours" sortKey="trainingHours" sortConfig={attendanceTable.sortConfig} onSort={attendanceTable.requestSort} align="right" className={th} />
                  <SortableHeader label="Trainer Hours" sortKey="trainerHours" sortConfig={attendanceTable.sortConfig} onSort={attendanceTable.requestSort} align="right" className={th} />
                  <SortableHeader label="Venue Check" sortKey="checkinInRange" sortConfig={attendanceTable.sortConfig} onSort={attendanceTable.requestSort} className={th} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attendanceTable.rows.map((r) => (
                  <tr key={r.email}>
                    <td className={td}>
                      <div className="font-medium text-gray-900">
                        {r.name}
                        {/* Not on the expected list — this tab is where walk-ins
                            are looked up (the Expected tab lists only the list). */}
                        {r.onList === false && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-50 text-amber-800">Walk-in</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{r.email}</div>
                    </td>
                    <td className={td}>{r.position || '—'}</td>
                    <td className={td}>{r.department || '—'}</td>
                    <td className={`${td} text-right`}>{r.trainingHours != null ? r.trainingHours.toFixed(1) : '—'}</td>
                    <td className={`${td} text-right`}>{r.trainerHours ? r.trainerHours.toFixed(1) : '—'}</td>
                    {/* Radius comes from the ROW, not the session's
                        venue as it stands now — they differ once a completed
                        session's venue is resized. No click through to
                        coordinates here either: this panel is ld:view. */}
                    <td className={td}>
                      <VenueCheckBadge
                        value={r.checkinInRange}
                        distanceM={r.checkinDistanceM}
                        radiusM={r.checkinRadiusM}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePager {...attendanceTable} className={pagerCls} />
          </div>
        )
      )}

      {/* Absence reasons */}
      {tab === 'absences' && (
        absences.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No absence reasons filed for this session.</p>
        ) : (
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="Staff" sortKey="name" sortConfig={absenceTable.sortConfig} onSort={absenceTable.requestSort} className={th} />
                  <SortableHeader label="Reason" sortKey="reasonCategory" sortConfig={absenceTable.sortConfig} onSort={absenceTable.requestSort} className={th} />
                  <SortableHeader label="Evidence" sortKey="evidence" sortConfig={absenceTable.sortConfig} onSort={absenceTable.requestSort} accessor={(r) => (r.evidence ? 1 : 0)} className={th} />
                  <SortableHeader label="Remarks" sortKey="remarks" sortConfig={absenceTable.sortConfig} onSort={absenceTable.requestSort} className={th} />
                  <SortableHeader label="Reviewed" sortKey="reviewedAt" sortConfig={absenceTable.sortConfig} onSort={absenceTable.requestSort} className={th} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {absenceTable.rows.map((r) => (
                  <tr key={r.email}>
                    <td className={td}>
                      <div className="font-medium text-gray-900">{r.name}</div>
                      <div className="text-xs text-gray-500">{r.email}</div>
                    </td>
                    <td className={td}>
                      <div>{r.reasonCategory}</div>
                      {r.reasonDetail && <div className="text-xs text-gray-500">{r.reasonDetail}</div>}
                    </td>
                    <td className={td}>{r.evidence ? 'Yes' : 'No'}</td>
                    <td className={`${td} max-w-[320px]`}><span className="break-words">{r.remarks || '—'}</span></td>
                    <td className={td}>
                      {r.reviewedAt
                        ? <span className="text-green-700">{formatDateShort(r.reviewedAt)}</span>
                        : <span className="text-amber-600">Pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePager {...absenceTable} className={pagerCls} />
          </div>
        )
      )}

      {/* Feedback */}
      {tab === 'feedback' && (
        feedback.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No feedback submitted for this session.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {chip('Avg Program', avg(feedback, 'programEffectiveness') ?? '—')}
              {chip('Avg Trainer', avg(feedback, 'trainerEffectiveness') ?? '—')}
              {chip('Avg Pre-competency', avg(feedback, 'preCompetency') ?? '—')}
              {chip('Avg Post-competency', avg(feedback, 'postCompetency') ?? '—')}
            </div>
            <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader label="Staff" sortKey="name" sortConfig={feedbackTable.sortConfig} onSort={feedbackTable.requestSort} className={th} />
                    <SortableHeader label="Designation" sortKey="position" sortConfig={feedbackTable.sortConfig} onSort={feedbackTable.requestSort} className={th} />
                    <SortableHeader label="Program" sortKey="programEffectiveness" sortConfig={feedbackTable.sortConfig} onSort={feedbackTable.requestSort} className={th} />
                    <SortableHeader label="Trainer" sortKey="trainerEffectiveness" sortConfig={feedbackTable.sortConfig} onSort={feedbackTable.requestSort} className={th} />
                    <SortableHeader label="Pre" sortKey="preCompetency" sortConfig={feedbackTable.sortConfig} onSort={feedbackTable.requestSort} className={th} />
                    <SortableHeader label="Post" sortKey="postCompetency" sortConfig={feedbackTable.sortConfig} onSort={feedbackTable.requestSort} className={th} />
                    <SortableHeader label="Comments" sortKey="comments" sortConfig={feedbackTable.sortConfig} onSort={feedbackTable.requestSort} className={th} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {feedbackTable.rows.map((r) => (
                    <tr key={r.email}>
                      <td className={td}>
                        <div className="font-medium text-gray-900">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.email}</div>
                      </td>
                      <td className={td}>{r.position || '—'}</td>
                      <td className={td}>{score(r.programEffectiveness, EFFECTIVENESS_LABELS)}</td>
                      <td className={td}>{score(r.trainerEffectiveness, EFFECTIVENESS_LABELS)}</td>
                      <td className={td}>{score(r.preCompetency, COMPETENCY_SHORT)}</td>
                      <td className={td}>{score(r.postCompetency, COMPETENCY_SHORT)}</td>
                      <td className={`${td} max-w-[320px]`}><span className="break-words">{r.comments || '—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePager {...feedbackTable} className={pagerCls} />
            </div>
          </div>
        )
      )}

      {/* Quiz — the paper, the head counts, and one row per person in the
          audience (plus anyone who sat it before dropping off the list). */}
      {tab === 'quiz' && quiz && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-700">
            <span className="font-medium text-gray-900">{quiz.quiz.title}</span>
            <span className="text-gray-500">{quiz.quiz.questionCount} question{quiz.quiz.questionCount === 1 ? '' : 's'} · {quiz.quiz.maxScore} pts</span>
            <span className="text-gray-500">Everyone expected sits it · {QUIZ_MODE_LABELS[quiz.quiz.quizMode] || quiz.quiz.quizMode}</span>
            <span className="text-gray-500">{quiz.quiz.passMark != null ? `Pass mark ${quiz.quiz.passMark}%` : 'Score only (no pass mark)'}</span>
            {quiz.quiz.mustPassCount > 0 && (
              <span
                className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-xs font-semibold"
                title="Questions that must be answered correctly — one wrong = Not passed, whatever the score"
              >
                {quiz.quiz.mustPassCount} must-answer
              </span>
            )}
          </div>
          {/* Completed = passed (or submitted, on a score-only paper);
              Outstanding = never sat, still open, or Not passed so far —
              the people the chasers keep writing to. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 max-w-3xl">
            <StatTile label="Assigned" value={quiz.stats.assigned} tone="slate" />
            <StatTile label="Completed" value={quiz.stats.done} tone="green" />
            <StatTile label="Outstanding" value={quiz.stats.outstanding} tone={quiz.stats.outstanding > 0 ? 'amber' : 'slate'} />
            <StatTile label="Avg Score" value={fmtPercent(quiz.stats.avgPercent)} tone="slate" />
            <StatTile
              label="Pass Rate"
              value={quiz.stats.passRate == null ? '—' : `${quiz.stats.passRate}%`}
              tone={quiz.stats.passRate == null ? 'slate' : (quiz.stats.passRate < 100 ? 'amber' : 'green')}
            />
          </div>
          {resetError && <p className="text-sm text-red-600">{resetError}</p>}
          {quizRows.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">
              Nobody is on the expected list yet — generate it from the check-in screen.
            </p>
          ) : (
            <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader label="Staff" sortKey="name" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} className={th} />
                    <SortableHeader label="Designation" sortKey="position" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} className={th} />
                    <SortableHeader label="Status" sortKey="status" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} accessor={(r) => QUIZ_STATUS_ORDER[r.status] ?? 9} className={th} />
                    <SortableHeader label="Attempts" sortKey="attempts" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} align="right" className={th} />
                    <SortableHeader label="Score" sortKey="score" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} align="right" className={th} />
                    <SortableHeader label="%" sortKey="percent" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} align="right" className={th} />
                    <SortableHeader label="Result" sortKey="passed" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} accessor={(r) => (r.passed == null ? -1 : (r.passed ? 1 : 0))} className={th} />
                    <SortableHeader label="Time Taken" sortKey="durationSeconds" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} align="right" className={th} />
                    <SortableHeader label="Focus Lost" sortKey="violations" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} align="right" className={th} />
                    <SortableHeader label="Submitted" sortKey="submittedAt" sortConfig={quizTable.sortConfig} onSort={quizTable.requestSort} className={th} />
                    <th className={`${th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {quizTable.rows.map((r) => (
                    <tr key={r.staffId}>
                      <td className={td}>
                        <div className="font-medium text-gray-900">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.email}</div>
                      </td>
                      <td className={td}>{r.position || '—'}</td>
                      <td className={td}><QuizStatusBadge status={r.status} /></td>
                      {/* Closed sittings so far (submitted or expired); a
                          retake in progress adds nothing until it closes. The
                          figures to the right are the latest graded sitting's. */}
                      <td className={`${td} text-right tabular-nums`}>{r.attempts > 0 ? r.attempts : <span className="text-gray-400">—</span>}</td>
                      <td className={`${td} text-right tabular-nums`}>{r.submittedAt ? `${r.score} / ${r.maxScore}` : '—'}</td>
                      <td className={`${td} text-right tabular-nums font-semibold`}>{r.submittedAt ? fmtPercent(r.percent) : '—'}</td>
                      <td className={td}>{r.submittedAt ? <QuizVerdictBadge passed={r.passed} /> : <span className="text-gray-400">—</span>}</td>
                      <td className={`${td} text-right tabular-nums`}>{r.submittedAt ? fmtDuration(r.durationSeconds) : '—'}</td>
                      {/* Times the quiz tab lost focus during the sitting — a
                          flag for HR to weigh, not a verdict. */}
                      <td className={`${td} text-right tabular-nums`}>
                        {r.violations > 0
                          ? <span className="text-amber-700">{r.violations}</span>
                          : <span className="text-gray-400">{r.status === 'not_started' ? '—' : '0'}</span>}
                      </td>
                      <td className={`${td} text-gray-500 whitespace-nowrap`}>{r.submittedAt ? formatDateTime(r.submittedAt) : '—'}</td>
                      <td className={`${td} text-right whitespace-nowrap`}>
                        {r.status !== 'not_started' && (
                          <button
                            type="button"
                            onClick={() => setViewAttempt({ staffId: r.staffId, name: r.name })}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                            title="View the answer sheet"
                          >
                            <Eye className="w-3.5 h-3.5" /> Answers
                          </button>
                        )}
                        {onResetAttempt && r.status !== 'not_started' && (
                          <button
                            type="button"
                            onClick={() => resetAttempt(r)}
                            disabled={resetting === r.staffId}
                            className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50"
                            title="Wipe every sitting of this person so they start the quiz over from attempt 1"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> {resetting === r.staffId ? 'Resetting…' : 'Reset'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePager {...quizTable} className={pagerCls} />
            </div>
          )}
          {viewAttempt && (
            <QuizAttemptModal
              sessionId={session.id}
              staffId={viewAttempt.staffId}
              name={viewAttempt.name}
              onClose={() => setViewAttempt(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SessionDetailPanel;
